<template>
  <div class="page-container">
    <!-- Header -->
    <the-header v-if="userAccessToken"></the-header>

    <!-- Loader -->
    <div v-if="loader" class="loader-overlay">
      <span id="dashboard-loader"></span>
    </div>

    <!-- Main Content (only shows after loader finishes) -->
    <div v-else>
      <base-breadcrumb></base-breadcrumb>

      <div role="main">
        <div class="page-title">
          <h1 role="heading" aria-level="1" tabindex="-1">{{ headerName }}</h1>
        </div>
        <div class="content-container">
          <div class="wd-100">
            <div class="left-content-container">
              <router-view :tabName="tabName"></router-view>
            </div>
          </div>
          <the-live-chat></the-live-chat>
          <div class="wd-10">
            <base-information :showInformation="true" :showResources="false" />
          </div>
        </div>

        <!-- Information Button -->
        <div class="information-btn desktop" v-if="!showInfo">
          <span @click="toggleInfoPopup">Information</span>
        </div>

        <!-- Information Pop-up -->
        <Teleport to="body">
          <div class="information-pop-up" v-if="showInfo">
            <button
              type="button"
              class="btn-close close"
              aria-label="Close information popup"
              @click="toggleInfoPopup"
            ></button>
            <base-information :showInformation="true" :showResources="false" />
          </div>
        </Teleport>
      </div>

      <!-- Footer -->
      <the-footer />
    </div>
  </div>
</template>


<script>
import { BaseInformation, BaseBreadcrumb } from '@microchip/common-vue-components';
import { TheHeader, TheFooter } from 'vue3-header-footer';
import { TheLiveChat } from 'vue3-live-chat';
import { mapGetters } from 'vuex';
import { signIn, acquireTokenSilent, checkTokenExpiry } from './security/authRedirect';
import { loginRequest } from './security/authConfig';

export default {
  name: 'App',
  components: {
    TheHeader,
    TheFooter,
    BaseInformation,
    BaseBreadcrumb,
    TheLiveChat,
  },
  data() {
    return {
      showInfo: false,
      isAuthenticated: false,
      user: '',
      accessToken: null,
      expireOn: null,
      loader: true,
      headerDataProp: '',
    };
  },
  computed: {
    ...mapGetters({
      userAccessToken: 'prochip/accessToken',
      headerHTML: 'headerHTML',
    }),
    tabName() {
      return this.$route.meta.tabName;
    },
    headerName() {
      return this.$route.meta.headerName;
    },
    breadcrumb() {
      return this.$route.meta.breadcrumb;
    },
  },
  watch: {
    userAccessToken(newToken) {
      if (newToken) {
        console.log("");
      }
    },
    headerHTML: {
      handler(value) {
        if (value) {
          this.headerDataProp = value;
          this.loader = false;
          setTimeout(() => {
            window.dispatchEvent(new Event('refreshAuthenticatedElements'));
          }, 2000);
        }
      },
      deep: true,
    },
  },
  methods: {
    toggleInfoPopup() {
      this.showInfo = !this.showInfo;
    },
    isPasswordChangeRequest() {
      const url = window.location.href;
      const fragment = url.split('#')[1];
      const params = new URLSearchParams(fragment?.split('?')[0]?.split('/')[1]);
      const state = params?.get('state');
      localStorage.setItem('stateValue', state);
      return state && state.includes('changepassword');
    },
    async authenticate(returnPath, ref) {
      try {
        const response = await checkTokenExpiry();
        if (response) {
          this.accessToken = response;
          this.isAuthenticated = true;
          ref.data().accessToken = response;
          ref.data().isAuthenticated = true;
        } else {
          this.signIn(returnPath);
        }
      } catch (error) {
        this.signIn(returnPath);
      }
    },
    async getUser(ref) {
      try {
        const user = await acquireTokenSilent(loginRequest);
        if (user) {
          const event = new CustomEvent('accessTokenCreated', {
            detail: { user },
          });
          window.dispatchEvent(event);
        }
        return user;
      } catch (err) {
        console.error('Error acquiring token:', err);
      }
    },
    signIn(returnPath) {
      returnPath ? signIn() : signIn();
    },
  },
  mounted() {
    // Reload on back nav to force fresh state
    window.addEventListener('pageshow', function (event) {
      const historyTraversal =
        event.persisted ||
        (typeof window.performance !== 'undefined' &&
          window.performance.navigation.type === 2);
      if (historyTraversal) {
        window.location.reload();
      }
    });

    // Check if user is in password change flow
    if (this.isPasswordChangeRequest()) {
      localStorage.setItem('changePasswordReload', true);
    }
  },
};
</script>

<style lang="scss">
@import 'bootstrap/dist/css/bootstrap.min.css';
@import "@microchip/common-vue-components/dist/commonvuecomponents.css";

  html, body {
    margin: 0px;
  }
  body {
    padding-top: 0px !important;
  }
  .page-title {
    background-color: var(--heading-bg-color);
    padding: 10px 0px;
    margin: 25px 0px 0px 0px;
  }
  .page-title h1 {
    color: var(--secondary-text-color);
    font-weight: 700;
    max-width: 1180px;
    margin-left: auto;
    margin-right: auto;
    white-space: nowrap;
    margin-bottom: 0px;
  }
  @media only screen and (max-width: 599px) {
    .page-title h1 {
      max-width: 90%;
    }
    .full-width {
      width: 100%;
    }
  }
  @media only screen and (min-width: 600px) and (max-width: 1199px) {
    .page-title h1 {
      max-width: 95%;
    }
  }
  .required:not(.embeddedServiceSidebarForm .required) {
    color: var(--form-field-error-bg-color);
    vertical-align: middle;
    font-size: 16px;
    margin-left: 5px;
    font-weight: 700;
  }
  .margin-top-45 {
    margin-top: 45px;
  }
  .margin-top-30 {
    margin-top: 30px;
  }
  .margin-top-20 {
    margin-top: 20px;
  }
  .margin-top-15 {
    margin-top: 15px;
  }
  .margin-bottom-5 {
    margin-bottom: 5px;
  }
  .margin-bottom-20 {
    margin-bottom: 20px;
  }
  .margin-left-10 {
    margin-left: 10px;
  }
  .text {
    color: var(--primary-text-color);
    font-size: 15px;
    font-family: Open Sans;
  }
  .full-width {
    width: 100%;
  }
  .text-area {
    resize: none;
    font-size: 16px;
    width: 100%;
  }
  .buttons-container {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
  }
  .individual-label {
    background-color: var(--form-field-bg-color);
    color: var(--secondary-text-color);
    padding: 5px 10px 5px 10px;
    border-top-left-radius: 5px;
    min-width: 150px;
    font-size: 16px;
    font-family: Open Sans;
  }
  @media only screen and (max-width: 1199px) {
    html, body {
      margin: 0px;
    }
    .desktop {
      display: none;
    }
    .mobile {
      display: block;
    }
  }
  @media only screen and (max-width:767px) {
    .page-title h1 {
      font-size: 1.2rem;
    }
    .wd-100 {
      padding: 0px;
    }
    .left-content-container {
      display: flex;
      flex-direction: column;
      padding: 0px;
      max-width: 95%;
      margin-right: auto;
      margin-left: auto;
    }
    .two-column-form-container {
      display: flex;
      flex-direction: column;
    }
    .prochip-form-container {
      margin-top: 10px;
    }
  }
  @media only screen and (min-width:768px) {
    .page-container {
      height: 100vh;
    }
    .page-title h1 {
      font-size: 1.2rem;
    }
    .left-content-container {
      display: flex;
      flex-direction: column;
      padding: 10px 0px;
    }
    .two-column-form-container {
      display: flex;
      flex-direction: row;
    }
    .wd-50 {
      flex: 1 1 50%;
      margin-right: 5%;
    }
    .wd-45 {
      flex: 1 1 45%;
    }
  }
  @media only screen and (max-width: 1199px) {
    .content-container {
      display: flex;
      flex-direction: column;
      padding: 5px 0px;
      max-width: 95%;
      margin-right: auto;
      margin-left: auto;
    }
  }
  @media only screen and (min-width: 1200px) {
    .wd-100 {
      flex: 1 1 100%;
      padding: 0px;
    }
    .wd-10 {
      display: none;
    }
    .content-container {
      display: flex;
      flex-direction: row;
      padding: 0px;
      max-width: 1180px;
      margin-right: auto;
      margin-left: auto;
    }
    .desktop {
      display: inline-block;
    }
    .mobile {
      display: none;
    }
  }
  .dropdown-backdrop {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: var(--disabled-link-color);
    z-index: 9;
  }
  .block {
    display: block;
  }
  .information-btn {
    position: fixed;
    width: 120px;
    height: auto;
    right: -50px;
    top: 400px;
    opacity: 0.96;
    background-color: var(--button-bg-color);
    border-radius: 3px 0 0 3px;
    white-space: nowrap;
    transform-origin: 0% 0%;
    -webkit-transform: rotate(-90deg);
    -moz-transform: rotate(-90deg);
    -o-transform: rotate(-90deg);
    -ms-transform: rotate(-90deg);
    transform: rotate(-90deg) translateX(50%);
    cursor: pointer;
  }
  .information-btn span {
    padding: 1rem;
    display: inline-block;
    color: var(--secondary-text-color);
  }
  .close {
    position: absolute;
    right: 5px;
    top: 5px;
  }
  .information-pop-up {
    width: 400px;
    position: fixed;
    z-index: 1;
    height: auto;
    top: 250px;
    right: 0;
    background-color: var(--information-bg-color);
    overflow-x: hidden;
    overflow-wrap: anywhere;
    word-break: break-word;
    animation: mymove 1s;
  }
  @keyframes mymove {
    from {right: -300px;}
    to {right: 0px;}
  }
  #prochip-license-agreement.modal, #prochip-success-modal.modal {
    padding: 0px;
    margin: 0px;
    top: 5% !important;
    left: 25% !important;
    --bs-modal-margin: 0rem;
    @media (max-width: 599px) {
      left: 2% !important;
      top: 2% !important;
      right: 2% !important;
    }
  }
  #prochip-license-agreement.modal {
    max-width: 55% !important;
    height: 90% !important;
    @media (max-width: 599px) {
      height: 96% !important;
      max-width: 96% !important;
    }
  }
  #prochip-success-modal.modal {
    max-width: 40% !important;
    height: auto !important;
    @media (max-width: 599px) {
      max-width: 96% !important;
      height: auto!important;
      top: 15% !important;
    }
    @media (min-width: 600px) and (max-width: 1023px) {
      height: auto !important;
      top: 15% !important;
      max-width: 50% !important;
    }
  }
  .blocker {
    z-index: 1055;
    background: transparent;
  }
  .modal-dialog {
    margin: 0 !important;
  }
  .modal a.close-modal {
    display: none;
  }
  #prochip-la-link {
    cursor: not-allowed;
    position: static;
  }
  .accordion-icon {
    width: 25px;
    height: 25px;
    padding-top: 0px;
    padding-left: 6px;
  }
  #dashboard-loader {
    position: fixed;
    left: 50%;
    left: 0;
    right: 0;
    margin: auto;
    top: 50%;
    z-index: 110;
    width: 80px;
    height: 80px;
    background-image: url("../src/assets/images/microchip-solid.svg");
    color: #ffffff;
    animation: smooth-beat 1s ease-in-out infinite;
    transition: opacity 0.5s ease;
  }
  @keyframes smooth-beat {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
  }
  .loader-overlay {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 100;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, .7);
    transition: .5s linear;
    
  }
  
</style>
